import Footer from '@components/organisms/FooterNew'
import Header from '@components/pages/home/LandingPage/Sections/Header'
import ContactUsPage from '@components/pages/contact-us/ContactUsPage'
import AuthorDetailsModal from '@components/pages/become-author/AuthorDetailsModal'
import { useAuth } from '@contexts/Auth'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'
import withLenis from '@components/HOCs/LenisWrapper'
import { useTranslation } from 'react-i18next'

const ContactUs = () => {
  const { t } = useTranslation(['contactUs'])
  const [showBecomeAuthorModal, setShowBecomeAuthorModal] = useState(false)
  const { user, isAuthenticated, setShowAuthModal } = useAuth()
  const freshLoggedIn = useRef(false)
  const router = useRouter()

  const locale = router.locale || 'en'
  const siteUrl = 'https://www.edugram.io'
  const localizedPath =
    locale === 'en' ? '/contact-us/' : `/${locale}/contact-us/`
  const canonicalUrl = `${siteUrl}${localizedPath}`
  const socialImage = `${siteUrl}/static/images/about/og-image.png`
  const seoTitle = t('meta.title', { defaultValue: t('title') })
  const seoDescription = t('meta.description', { defaultValue: t('subtitle') })
  const seoOgDescription = t('meta.ogDescription', {
    defaultValue: seoDescription,
  })

  const contactPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: seoTitle,
    description: seoDescription,
    url: canonicalUrl,
    inLanguage: locale,
    mainEntity: {
      '@type': 'Organization',
      name: 'Edugram',
      url: siteUrl,
      email: 'support@edugram.io',
      contactPoint: [
        {
          '@type': 'ContactPoint',
          contactType: 'customer support',
          email: 'support@edugram.io',
          availableLanguage: ['en', 'uk', 'es', 'fr', 'de', 'it'],
        },
      ],
    },
  }

  useEffect(() => {
    if (user && !user?.isAuthor && freshLoggedIn.current) {
      setShowBecomeAuthorModal(true)
    }
    return () => {
      freshLoggedIn.current = false
    }
  }, [user])

  const handleBecomeCreator = () => {
    if (!isAuthenticated) {
      setShowAuthModal(true)
      freshLoggedIn.current = true
      return
    }
    if (user?.isAuthor) router.push('/author')
    else setShowBecomeAuthorModal(true)
  }

  return (
    <div>
      <Head>
        <title>{seoTitle}</title>
        <meta name="title" content={seoTitle} />
        <meta name="description" content={seoDescription} />
        <meta name="robots" content="index,follow,max-image-preview:large" />

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Edugram" />
        <meta property="og:locale" content={locale} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoOgDescription} />
        <meta property="og:image" content={socialImage} />
        <meta property="og:image:alt" content={seoTitle} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoOgDescription} />
        <meta name="twitter:image" content={socialImage} />

        <link rel="canonical" href={canonicalUrl} />
        <script
          id="contact-page-schema"
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(contactPageSchema),
          }}
        />
      </Head>
      <Header handleBecomeCreator={handleBecomeCreator} />
      <ContactUsPage />
      {showBecomeAuthorModal && (
        <AuthorDetailsModal
          isOpen={showBecomeAuthorModal}
          onClose={(ask?: boolean) => {
            if (ask) {
              if (confirm('Are you sure you want to close?')) {
                setShowBecomeAuthorModal((prev) => !prev)
              }
            } else {
              setShowBecomeAuthorModal((prev) => !prev)
            }
          }}
        />
      )}
      <div className="max-w-desktop mx-auto sm:px-4">
        <Footer />
      </div>
    </div>
  )
}

export default withLenis(ContactUs)

export async function getStaticProps({ locale }: { locale: string }) {
  return {
    props: {
      ...(await serverSideTranslations(locale, [
        'common',
        'authentication',
        'footer',
        'courses',
        'signUp',
        'home',
        'wishlistDropdown',
        'errors',
        'homeLanding/header',
        'reportProblem',
        'contactUs',
      ])),
    },
  }
}
